// =====================================================
// Swiss Finance OS - Transaction Review Hooks
// React Query hooks for transaction review workflow
// Agent 1: Review Queue UI
// =====================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import * as reviewQueueService from '@/services/reviewQueueService';
import type { ReviewTransaction, ReviewQueueFilters, VATWarning } from '@/services/reviewQueueService';

export interface UseReviewQueueOptions {
  mandateId?: string;
  filters?: ReviewQueueFilters;
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Hook for fetching transaction review queue
 */
export function useReviewQueue(options: UseReviewQueueOptions = {}) {
  const {
    mandateId,
    filters = {},
    enabled = true,
    refetchInterval = false, // Use realtime updates instead
  } = options;

  return useQuery({
    queryKey: ['review-queue', mandateId, filters],
    queryFn: () => {
      if (!mandateId) {
        throw new Error('mandateId is required for review queue');
      }
      return reviewQueueService.getTransactionsForReview(mandateId, filters);
    },
    enabled: enabled && !!mandateId,
    refetchInterval,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for fetching VAT warnings for a transaction
 */
export function useVATWarnings(transactionId: string | null, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['vat-warnings', transactionId],
    queryFn: () => {
      if (!transactionId) {
        return null;
      }
      return reviewQueueService.getVATWarnings(transactionId);
    },
    enabled: enabled && !!transactionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook for approving a transaction
 */
export function useApproveTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (transactionId: string) => {
      return reviewQueueService.approveTransaction(transactionId);
    },
    onSuccess: (_, transactionId) => {
      // Invalidate review queue queries
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });

      toast({
        title: 'Transaction Approved',
        description: 'Transaction has been approved successfully.',
        duration: 2000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Approval Failed',
        description: error.message || 'Failed to approve transaction.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for editing a transaction
 */
export function useEditTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      transactionId,
      updates,
    }: {
      transactionId: string;
      updates: {
        debit_account?: string;
        credit_account?: string;
        vat_rate?: number;
        vat_code?: string;
      };
    }) => {
      return reviewQueueService.updateTransaction(transactionId, updates);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['vat-warnings'] });

      toast({
        title: 'Transaction Updated',
        description: 'Classification has been updated successfully.',
        duration: 2000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update transaction.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for rejecting a transaction
 */
export function useRejectTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (transactionId: string) => {
      return reviewQueueService.rejectTransaction(transactionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      toast({
        title: 'Transaction Rejected',
        description: 'Transaction has been marked as rejected.',
        duration: 2000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Rejection Failed',
        description: error.message || 'Failed to reject transaction.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for bulk approving transactions
 */
export function useBulkApproveTransactions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (transactionIds: string[]) => {
      return reviewQueueService.bulkApproveTransactions(transactionIds);
    },
    onSuccess: (_, transactionIds) => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });

      toast({
        title: 'Bulk Approval Successful',
        description: `Successfully approved ${transactionIds.length} transactions.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk Approval Failed',
        description: error.message || 'Failed to approve transactions.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for bulk setting VAT rate
 */
export function useBulkSetVATRate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ transactionIds, vatRate }: { transactionIds: string[]; vatRate: number }) => {
      return reviewQueueService.bulkSetVATRate(transactionIds, vatRate);
    },
    onSuccess: (_, { transactionIds, vatRate }) => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['vat-warnings'] });

      toast({
        title: 'VAT Rate Updated',
        description: `Set VAT rate to ${vatRate}% for ${transactionIds.length} transactions.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'VAT Update Failed',
        description: error.message || 'Failed to update VAT rates.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for bulk assigning debit account
 */
export function useBulkAssignAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ transactionIds, debitAccount }: { transactionIds: string[]; debitAccount: string }) => {
      return reviewQueueService.bulkAssignAccount(transactionIds, debitAccount);
    },
    onSuccess: (_, { transactionIds, debitAccount }) => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      toast({
        title: 'Account Assigned',
        description: `Assigned account ${debitAccount} to ${transactionIds.length} transactions.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Account Assignment Failed',
        description: error.message || 'Failed to assign account.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for getting review progress for a mandate
 */
export function useReviewProgress(mandateId: string | null, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['review-progress', mandateId],
    queryFn: () => {
      if (!mandateId) {
        throw new Error('mandateId is required');
      }
      return reviewQueueService.getReviewProgress(mandateId);
    },
    enabled: enabled && !!mandateId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for booking a transaction
 */
export function useBookTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const { createBookingFromTransaction } = await import('@/services/bookingService');
      return createBookingFromTransaction(transactionId);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });

      toast({
        title: 'Booking Created',
        description: `Journal Entry #${result.entry_number} created successfully.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Booking Failed',
        description: error.message || 'Failed to create booking.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for bulk booking transactions
 */
export function useBulkBookTransactions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (transactionIds: string[]) => {
      const { bulkCreateBookings } = await import('@/services/bookingService');
      return bulkCreateBookings(transactionIds);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });

      toast({
        title: 'Bulk Booking Complete',
        description: `Successfully booked ${result.success} of ${result.total} transactions.${result.errors && result.errors.length > 0 ? ` ${result.errors.length} had errors.` : ''}`,
        duration: 4000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk Booking Failed',
        description: error.message || 'Failed to create bookings.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}
